import { useEffect, useMemo } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import AIConfigManager from './components/AIConfigManager';
import AttributeManager from './components/AttributeManager';
import Login from './components/Auth/Login';
import AutomationManager from './components/AutomationManager';
import BannerManager from './components/BannerManager';
import BillingManager from './components/BillingManager';
import BlogManager from './components/BlogManager';
import CategoryManager from './components/CategoryManager';
import ModuleProtectedRoute from './components/common/ModuleProtectedRoute';
import CouponManager from './components/CouponManager';
import Dashboard from './components/Dashboard';
import DashboardHome from './components/DashboardHome';
import ModuleManager from './components/ModuleManager';
import OrderManager from './components/OrderManager';
import ProductManager from './components/ProductManager';
import ReviewsManager from './components/ReviewsManager';
import SEOManager from './components/SEOManager';
import SettingsManager from './components/SettingsManager';
import UploadImages from './components/UploadImages';
import UserManager from './components/UserManager';
import WorkflowManager from './components/WorkflowManager';
import WorkshopManager from './components/WorkshopManager';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ModuleProvider } from './context/ModuleContext';
import { TenantProvider } from './context/TenantContext';
import { setupAxiosInterceptors } from './utils/axiosInterceptor';
import { getBasePath } from './utils/tenantDetection';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Component to display current tenant info
// const TenantInfo = () => {
//   const { currentTenant, isTenantMode } = useTenant();
  
//   if (!isTenantMode || !currentTenant) return null;
  
//   return (
//     <div style={{ 
//       background: '#f8f9fa', 
//       padding: '10px 20px', 
//       borderBottom: '1px solid #dee2e6',
//       textAlign: 'center',
//       position: 'sticky',
//       top: 0,
//       zIndex: 1000
//     }}>
//       <small style={{ color: '#6c757d' }}>
//         🏢 Viewing as: <strong>{currentTenant}</strong> (Tenant Mode)
//       </small>
//     </div>
//   );
// };

function App() {
  // CRITICAL: Memoize basePath to prevent Router remount on every render
  // This prevents full page refresh when components re-render
  const basePath = useMemo(() => {
    // Get base path for tenant (e.g., '/shilpani' if URL is /shilpani/login)
    // Only calculate once on mount, not on every render
    return getBasePath();
  }, []); // Empty dependency array - only calculate once

  useEffect(() => {
    // Setup axios interceptors to handle suspension/expiration errors
    setupAxiosInterceptors();
  }, []);

  return (
    <AuthProvider>
      <TenantProvider>
        <ModuleProvider>
          <Router basename={basePath}>
            <div className="App">
              {/* <TenantInfo /> */}
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  }
                >
                <Route 
                  index 
                  element={
                    <ModuleProtectedRoute moduleCode="ANALYTICS">
                      <DashboardHome />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="orders" 
                  element={
                    <ModuleProtectedRoute moduleCode="ORDERS">
                      <OrderManager />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="coupons" 
                  element={
                    <ModuleProtectedRoute moduleCode="COUPONS">
                      <CouponManager />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route path="modules" element={<ModuleManager />} />
                <Route path="upload" element={<UploadImages />} />
                <Route 
                  path="categories" 
                  element={
                    <ModuleProtectedRoute moduleCode="CATEGORIES">
                      <CategoryManager />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="products" 
                  element={
                    <ModuleProtectedRoute moduleCode="PRODUCTS">
                      <ProductManager />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="attributes" 
                  element={
                    <ModuleProtectedRoute moduleCode="PRODUCTS">
                      <AttributeManager />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="banners" 
                  element={
                    <ModuleProtectedRoute moduleCode="BANNERS">
                      <BannerManager />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="blogs" 
                  element={
                    <ModuleProtectedRoute moduleCode="BLOGS">
                      <BlogManager />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="workshops" 
                  element={
                    <ModuleProtectedRoute moduleCode="WORKSHOPS">
                      <WorkshopManager />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="seo" 
                  element={
                    <ModuleProtectedRoute moduleCode="SEO">
                      <SEOManager />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="reviews" 
                  element={
                    <ModuleProtectedRoute moduleCode="REVIEWS">
                      <ReviewsManager />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="automation" 
                  element={
                    <ModuleProtectedRoute moduleCode="AUTOMATION">
                      <AutomationManager />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="ai-config" 
                  element={
                    <ModuleProtectedRoute moduleCode="AI_CHATBOT">
                      <AIConfigManager />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="workflows" 
                  element={
                    <ModuleProtectedRoute moduleCode="WORKFLOWS">
                      <WorkflowManager />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route path="settings" element={<SettingsManager />} />
                <Route path="users" element={<UserManager />} />
                <Route path="billing" element={<BillingManager />} />
              </Route>
            </Routes>
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={true}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              limit={3}
              enableMultiContainer={false}
            />
          </div>
        </Router>
      </ModuleProvider>
    </TenantProvider>
    </AuthProvider>
  );
}

export default App;

